import { useEffect, useState } from "react";
import "./Profile.css";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from 'react-toastify';
import { userRequest } from "../../RequestMethods";
import { authSuccess } from "../../Redux/slices/AuthSlice";
import { ProfileImagePreview } from "../../Components/ProfileImagePreview/ProfileImagePreview";
import {
  UilUser,
  UilEnvelope,
  UilPhone,
  UilMapMarker,
  UilGlobe,
  UilLocationPoint,
  UilBuilding,
  UilInstagram,
  UilMusicNote,
} from "@iconscout/react-unicons";

export const Profile = () => {
  const { currentUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [userData, setUserData] = useState({
    img: "",
    name: "",
    surname: "",
    email: "",
    number: "",
    address: "",
    country: "",
    city: "",
    postalCode: "",
    instagram: "",
    tiktok: "",
  });

  const [saving, setSaving] = useState(false);

  // Charger les infos du currentUser
  useEffect(() => {
    if (currentUser?.worker) {
      const { img, name, surname, email, number, address, country, city, postalCode, instagram, tiktok } = currentUser.worker;
      setUserData({ img, name, surname, email, number, address, country, city, postalCode, instagram, tiktok });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (imgDataUrl) => {
    setUserData((prev) => ({ ...prev, img: imgDataUrl }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await userRequest.put(`workers/${currentUser.worker._id}`, userData);
      // The PUT response has the fresh worker doc (incl. the new img) — without
      // this, Redux keeps serving the stale worker from login time, so neither
      // this page nor the TopBar avatar would ever reflect the change.
      dispatch(authSuccess({ ...currentUser, worker: res.data.worker }));
      toast.success("Profil mis à jour avec succès !");
    } catch (err) {
      console.error("Erreur lors de la mise à jour :", err);
      toast.error("Erreur lors de la mise à jour du profil.");
    } finally {
      setSaving(false);
    }
  };

  const renderField = ({ icon, label, name, placeholder, type = "text" }) => (
    <div className="form-group">
      <label>
        {icon}
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={userData[name] || ""}
        onChange={handleChange}
        placeholder={placeholder}
      />
    </div>
  );

  const roleName = currentUser?.worker?.roleId?.name || (currentUser?.worker?.isAdmin ? "Super Admin" : "Staff");
  const joinedSince = currentUser?.worker?.createdAt
    ? new Date(currentUser.worker.createdAt).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : null;

  return (
    <div className="profile-page">
      <div className="profile-hero">
        <div className="profile-avatar-wrap">
          <ProfileImagePreview onImageUpload={handleImageUpload} initialPreview={currentUser?.worker?.img} />
        </div>
        <div className="profile-hero-info">
          <h1 className="profile-name">{userData.name} {userData.surname}</h1>
          <div className="profile-hero-meta">
            <span className="role-badge">{roleName}</span>
            {joinedSince && <span className="joined-since">Membre depuis {joinedSince}</span>}
          </div>
        </div>
        <div className="profile-hero-contact">
          <span><UilEnvelope size={15} />{userData.email}</span>
          {userData.number && <span><UilPhone size={15} />{userData.number}</span>}
        </div>
      </div>

      <div className="profile-body">
        <form className="profile-form" onSubmit={handleSubmit}>
          <section className="form-section">
            <div className="section-heading">
              <h2>Informations personnelles</h2>
              <p>Vos informations de base et vos coordonnées.</p>
            </div>
            <div className="form-grid">
              {renderField({ icon: <UilUser size={16} />, label: "Nom", name: "name", placeholder: "Nom" })}
              {renderField({ icon: <UilUser size={16} />, label: "Prénom", name: "surname", placeholder: "Prénom" })}
              {renderField({ icon: <UilEnvelope size={16} />, label: "E-mail", name: "email", placeholder: "E-mail", type: "email" })}
              {renderField({ icon: <UilPhone size={16} />, label: "Téléphone", name: "number", placeholder: "Numéro de téléphone" })}
            </div>
          </section>

          <section className="form-section">
            <div className="section-heading">
              <h2>Adresse</h2>
              <p>Utilisée pour votre profil interne.</p>
            </div>
            <div className="form-grid">
              {renderField({ icon: <UilMapMarker size={16} />, label: "Adresse", name: "address", placeholder: "Adresse" })}
              {renderField({ icon: <UilGlobe size={16} />, label: "Pays", name: "country", placeholder: "Pays" })}
              {renderField({ icon: <UilBuilding size={16} />, label: "Ville", name: "city", placeholder: "Ville" })}
              {renderField({ icon: <UilLocationPoint size={16} />, label: "Code Postal", name: "postalCode", placeholder: "Code Postal" })}
            </div>
          </section>

          <section className="form-section">
            <div className="section-heading">
              <h2>Réseaux sociaux</h2>
              <p>Optionnel.</p>
            </div>
            <div className="form-grid">
              {renderField({ icon: <UilInstagram size={16} />, label: "Instagram", name: "instagram", placeholder: "Instagram" })}
              {renderField({ icon: <UilMusicNote size={16} />, label: "TikTok", name: "tiktok", placeholder: "TikTok" })}
            </div>
          </section>

          <div className="submit-section">
            <button type="submit" className="submit-btn" disabled={saving}>
              {saving ? "Enregistrement..." : "Enregistrer les modifications"}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer position="top-right" />
    </div>
  );
};
